export type StructuredAnswerBlock =
  | {
    type: 'paragraph';
    items: string[];
  }
  | {
    type: 'bullet-list' | 'ordered-list';
    items: string[];
  };

function splitSemicolonListTail(value: string): string[] {
  return String(value || '')
    .split(/\s*;\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part, index, parts) => {
      if (index < parts.length - 1) {
        return part.replace(/[.;,:]+$/, '').trim();
      }
      return part;
    });
}

export function normalizeListAnswerBody(body: string): string {
  const trimmed = String(body || '').trim();
  if (!trimmed) {
    return '';
  }

  if (/\r?\n\s*[-*]\s+/.test(trimmed) || /\r?\n\s*\d+\.\s+/.test(trimmed)) {
    return trimmed;
  }

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex <= 0 || colonIndex >= trimmed.length - 1) {
    return trimmed;
  }

  const intro = trimmed.slice(0, colonIndex + 1).trim();
  const tail = trimmed.slice(colonIndex + 1).trim();
  if (!tail.includes(';')) {
    return trimmed;
  }

  const listParts = splitSemicolonListTail(tail);
  if (listParts.length < 2) {
    return trimmed;
  }

  return `${intro}\n${listParts.map((part) => `- ${part}`).join('\n')}`;
}

export function parseStructuredAnswerBody(body: string): StructuredAnswerBlock[] {
  const normalizedBody = normalizeListAnswerBody(body);
  const lines = normalizedBody
    .split(/\r?\n/)
    .map((line) => line.trim());
  const blocks: StructuredAnswerBlock[] = [];
  let activeBlock: StructuredAnswerBlock | null = null;

  const flushActiveBlock = () => {
    if (!activeBlock || !activeBlock.items.length) {
      activeBlock = null;
      return;
    }
    blocks.push(activeBlock);
    activeBlock = null;
  };

  for (const line of lines) {
    if (!line) {
      flushActiveBlock();
      continue;
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      if (!activeBlock || activeBlock.type !== 'bullet-list') {
        flushActiveBlock();
        activeBlock = {
          type: 'bullet-list',
          items: [],
        };
      }
      activeBlock.items.push(bulletMatch[1].trim());
      continue;
    }

    const orderedMatch = line.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      if (!activeBlock || activeBlock.type !== 'ordered-list') {
        flushActiveBlock();
        activeBlock = {
          type: 'ordered-list',
          items: [],
        };
      }
      activeBlock.items.push(orderedMatch[1].trim());
      continue;
    }

    if (!activeBlock || activeBlock.type !== 'paragraph') {
      flushActiveBlock();
      activeBlock = {
        type: 'paragraph',
        items: [],
      };
    }
    activeBlock.items.push(line);
  }

  flushActiveBlock();
  return blocks;
}
